'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { FaBookOpen, FaPlay } from 'react-icons/fa'
import { useAuth } from '../../contexts/AuthContext'
import { capitalizeNames } from '../../helpers/helpers'
import {
  safeGetItem,
  safeSessionGetItem,
  safeSessionSetItem,
} from '../../helpers/safeStorage'
import { storyContinue, storyShowcase } from '../../lib/date-game'
import { fetchDateGameFeed } from './dateGameApi'

// The "Stories" row, at the top of Discover (2026-10-05; it was on Explore before) (player-facing name since 2026-10-04: "Stories", interactive visual
// novels; identifiers keep "date game"): a horizontal, snap-scrolling strip of wide cards, one
// per published story, fed by GET /api/date_game/feed.
//
// Each card shows the publisher's showcase stills (`showcase`, non-explicit; else the cover —
// nothing else from the story is ever shown here) SIDE BY SIDE: they are 2:3 portraits, so three
// of them fill a 2:1 card uncropped (two on phones, 4:3). A single still crossfading in a 16:9
// frame cropped each one to a zoomed-in band (product owner, 2026-10-04). The rest follows
// ChatCard: the title and logline over a bottom gradient (no "name, age" line — the story, not the
// character, is what the card sells; product owner, 2026-10-04), a frosted chapter-count
// pill, and a frosted Play button that turns brand pink on card hover — "Continue" when this
// browser has started the story (lib/date-game storyContinue, over the player's saved state).
// The whole card is the link, and Play is a span inside it, not a control of its own. A Chat
// button beside Play (ChatCard's) was built and dropped the same day: the product owner found two
// actions on a story card confusing (2026-10-04).
//
// Renders NOTHING — no heading, no placeholder — when disabled (safe mode / safe country: the
// stories are NSFW and the backend returns [] there too), or once the feed has answered empty
// (or failed with nothing cached), so a Discover with no published stories looks as before.
//
// Pop-in (2026-10-04): the row used to render nothing until the feed answered, then appeared a
// second after the page and pushed what was below it down. Now:
// - the last good feed is kept per account in sessionStorage and read synchronously on the
//   first render, so a reload or a return visit in the tab paints the cards at once, while a
//   background fetch refreshes them;
// - with nothing cached, the heading and two skeleton cards of the real cards' exact size hold
//   the space until the answer. Only an empty or failed cold answer still removes the section
//   and shifts the page — rare (stories are published), and an empty heading would be worse.
// sessionStorage rather than localStorage: a new tab or tomorrow's visit should not open on a
// list that a since-published or unpublished story has made wrong, and the background refresh
// would visibly swap it. Within a tab the list barely changes. Through safeStorage, as Safari
// throws on touching either store when cookies are blocked.
//
// One fetch per page load: what answered is also kept at module scope, so the tab switches and
// filter changes that remount the Featured block (and this row with it) neither refetch nor
// re-show a skeleton.
let answered = null // { uid, games } — this page load's successful answer
// A card's width in the row: most of a phone, then three quarters of the row, which still shows the
// next card's edge as a scroll hint. Wide rows used to halve it (two cards a row), which with one
// story left a ~500 px card and an empty half beside it; the product owner asked for it bigger twice
// (2026-10-06). Capped at 64rem (1024 px) so a wide monitor does not get a billboard.
const CARD_WIDTH = 'snap-start shrink-0 w-[86%] @xl:w-[75%] @xl:max-w-[64rem]'

let failedUid // the account whose fetch failed this page load: no skeleton on a retry
let seeded = null // { uid, games } — the sessionStorage copy, parsed once per page load

const feedKey = (uid) => `dateGame:feed:${uid || 'guest'}`

// The best list known without waiting: this page load's answer, else this tab's last one.
// null = nothing known (show the skeleton).
function knownFeed(uid) {
  if (answered && answered.uid === uid) return answered.games
  if (!seeded || seeded.uid !== uid) {
    let games = null
    try {
      const parsed = JSON.parse(safeSessionGetItem(feedKey(uid)) || 'null')
      if (Array.isArray(parsed))
        games = parsed.filter((g) => g && g.character_id)
    } catch {
      // A corrupt copy is no copy: the fetch below replaces it.
    }
    seeded = { uid, games }
  }
  return seeded.games
}

// What this browser saved for chapter n of a story — the keys DateGame writes (chapter 1 keeps
// the original, unsuffixed ones).
function savedChapter(cid, n) {
  const part = n > 1 ? `:ch${n}` : ''
  const read = (k) => {
    try {
      return JSON.parse(safeGetItem(`dateGame:${cid}${part}:${k}`) || 'null')
    } catch {
      return null
    }
  }
  return { play: read('play'), progress: read('progress') }
}

// Same height and widths as StoryCard, so the cards replace it without moving anything.
function SkeletonCard() {
  return (
    <li aria-hidden="true" className={CARD_WIDTH}>
      <div className="aspect-[4/3] @xl:aspect-[2/1] animate-pulse rounded-2xl bg-[var(--foreground-bg-color)]" />
    </li>
  )
}

function StoryCard({ g, t }) {
  const stills = storyShowcase(g)
  const chapterNumbers = Array.isArray(g.chapters)
    ? g.chapters.map((c) => Number(c.chapter))
    : [1]
  const chapterCount = Math.max(1, chapterNumbers.length)
  // Read after mount only: storage is per browser and the server render has none.
  const [cont, setCont] = useState(null)
  useEffect(() => {
    setCont(
      storyContinue(chapterNumbers, (n) => savedChapter(g.character_id, n)),
    )
  }, [g.character_id]) // chapters come with the same feed entry

  const href = `/date/${encodeURIComponent(g.character_id)}${
    cont && cont.chapter > 1 ? `?chapter=${cont.chapter}` : ''
  }`
  const name = capitalizeNames(g.name || '')

  return (
    <li className={CARD_WIDTH}>
      <Link
        href={href}
        data-track={cont ? 'stories.card_continue' : 'stories.card'}
        aria-label={
          cont
            ? `${t('dateContinue')} — ${g.title || name}, ${t('dateChapterN', { n: cont.chapter })}`
            : t('dateButtonAria', { name })
        }
        // One height for every card in the row, whatever its number of stills. `isolate`: WebKit
        // otherwise paints the scaled strip past the rounded corners while it transforms.
        className="group relative isolate block aspect-[4/3] @xl:aspect-[2/1] overflow-hidden rounded-2xl shadow-xl transition-shadow duration-200 hover:shadow-2xl focus-visible:outline-none"
      >
        {/* The stills zoom on hover as ONE picture (product owner, 2026-10-04): each tile
            scaling in its own cell read as three separate zooms. Only this strip scales — the
            gradient, text, pill and button stay put — and the card's overflow-hidden clips it.
            Its background shows through the 1px gaps between the tiles. */}
        <div
          className={`absolute inset-0 grid ${
            stills.length > 1 ? 'grid-cols-2' : ''
          } ${
            stills.length > 2 ? '@xl:grid-cols-3' : ''
          } gap-px bg-[var(--foreground-bg-color)] transition-transform duration-300 group-hover:scale-[1.02]`}
        >
          {stills.map((src, k) => (
            <div
              key={src}
              className={`relative overflow-hidden ${k === 2 ? 'hidden @xl:block' : ''}`}
            >
              <img
                src={src}
                alt={k === 0 ? g.title || name : ''}
                aria-hidden={k === 0 ? undefined : true}
                loading="lazy"
                decoding="async"
                draggable={false}
                className="absolute inset-0 h-full w-full object-cover object-top brightness-[0.9]"
              />
            </div>
          ))}
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-none" />

        {/* ChatCard's layout: the text full width above a bottom row of the frosted pill (left) and
            the button (right), so a phone card does not squeeze the title beside the button. */}
        <div className="absolute inset-x-0 bottom-0 px-4 pb-16 text-white">
          <p className="truncate text-xl @xl:text-2xl @3xl:text-3xl font-extrabold leading-tight tracking-tight drop-shadow-sm">
            {g.title || name}
          </p>
          {g.logline && (
            <p className="mt-1 text-sm leading-5 font-medium tracking-tight text-white/70 drop-shadow-sm line-clamp-2">
              {g.logline}
            </p>
          )}
        </div>

        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full border border-white/10 bg-black/25 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-md">
          <FaBookOpen className="text-sm" aria-hidden="true" />
          {chapterCount > 1
            ? t('dateChapterCount', { n: chapterCount })
            : t('dateChapterCountOne')}
        </div>

        <span className="absolute bottom-3 right-3 flex h-10 items-center rounded-full border border-white/10 bg-black/25 px-4 text-white backdrop-blur-md transition-colors duration-300 group-hover:bg-[var(--main-bg-color)]">
          <FaPlay className="mr-1.5 text-xs" aria-hidden="true" />
          <span className="text-sm font-semibold">
            {cont ? t('dateContinue') : t('dateButton')}
          </span>
        </span>

        {/* Keyboard focus ring, drawn last so the pictures cannot cover it (an inset ring on the
            link itself paints under its children). */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-2xl ring-inset ring-white group-focus-visible:ring-2"
        />
      </Link>
    </li>
  )
}

export default function DateGamesRow({ enabled }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const uid = user?.uid || null
  const [fetched, setFetched] = useState(null) // { uid, games } — this mount's answer

  // Read during render, and that is hydration-safe only because this never runs on the server
  // or in the hydrating render: `enabled` needs AuthContext's `safe`, which starts null and is
  // set in an effect, so `enabled` (safe === false) is false on the server and in the first client
  // render wherever the row is mounted. Keep the storage read behind `enabled` for that reason.
  const games = !enabled
    ? null
    : fetched && fetched.uid === uid
      ? fetched.games
      : (knownFeed(uid) ?? (failedUid === uid ? [] : null))

  useEffect(() => {
    if (!enabled) return
    if (answered && answered.uid === uid) return
    let cancelled = false
    fetchDateGameFeed(user, 12).then((res) => {
      if (res.ok) {
        const list = Array.isArray(res.data?.games)
          ? res.data.games.filter((g) => g && g.character_id)
          : []
        answered = { uid, games: list }
        safeSessionSetItem(feedKey(uid), JSON.stringify(list))
        if (!cancelled) setFetched(answered)
        return
      }
      // A failure keeps a cached list on screen; with none it removes the skeleton. The next
      // mount retries, without the skeleton.
      failedUid = uid
      if (!cancelled && !knownFeed(uid)) setFetched({ uid, games: [] })
    })
    return () => {
      cancelled = true
    }
  }, [enabled, uid]) // `user` is read through `uid`: a new object for the same account is no refetch

  if (!enabled || (games && games.length === 0)) return null

  return (
    <section
      aria-labelledby="date-games-heading"
      aria-busy={games ? undefined : true}
      // Sized by its own width, not the viewport (container queries, 2026-10-05): the same row sits
      // full-width in Explore and in Discover's narrower column beside the chat list, where viewport
      // breakpoints made the cards tiny.
      className="@container mb-6 overflow-hidden rounded-xl bg-[var(--middle-bg-color)] py-3 md:p-4"
    >
      <div className="mb-3 md:mb-4 px-3 md:px-0 max-w-xl">
        <h2
          id="date-games-heading"
          className="text-2xl md:text-3xl font-bold text-white my-2 flex items-center"
        >
          <FaBookOpen className="text-[var(--main-bg-color)] mr-2 shrink-0" />
          {t('dateGamesTitle')}
          {/* The site's inline "New!" label (the Video model / Style labels in Inputs), at text-xs
              rather than 10px to sit beside a 30px heading. */}
          <span className="ml-2.5 shrink-0 rounded-full bg-[var(--main-bg-color)] px-2 py-0.5 text-xs font-bold text-white">
            {t('new')}
          </span>
        </h2>
        <p className="mt-1 text-sm text-gray-400">{t('dateGamesSubtitle')}</p>
      </div>

      <ul className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-px-3 md:scroll-px-0 px-3 md:px-0 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {games ? (
          games.map((g) => <StoryCard key={g.character_id} g={g} t={t} />)
        ) : (
          <>
            <SkeletonCard />
            <SkeletonCard />
          </>
        )}
      </ul>
    </section>
  )
}
